import React, {useEffect} from 'react';

import * as OrganizationActions from 'actions/OrganizationActions';
// @ts-expect-error
import * as UserActions from 'actions/UserActions';
import * as analytics from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {EntityStoreUser} from 'constants/EntityStoreConstants';
import JoinOrganizationSearch from 'parties/organizations/JoinOrganizationSearch';
import {useJoinOrganizationSearch} from 'parties/organizations/useJoinOrganizationSearch';
import getTestProps from 'test/lib/cypress/getTestProps';
import Modal from 'ui-library/modals/Modal';

// @ts-expect-error
import {getOrgIdsWithUserJoinRequest} from './OrganizationUtil';
import OutboundOrganizationRequest from './OutboundOrganizationRequest';
import PendingInvites from './PendingInvites';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const renderPendingInvites = (currentUser: EntityStoreUser) => [
  <h4 className="u-no-spacing-top" key="invites-title">
    Pending Invites
  </h4>,
  currentUser.currentUserInboundRequests != null
    ? Array.from(currentUser.currentUserInboundRequests).map((request) => (
        <div className="u-small-spacing-top" key={`invites-${request.organization.id}`}>
          <OutboundOrganizationRequest request={request} collapsed={false} />
        </div>
      ))
    : undefined,
  currentUser.pendingOrgInvites ? (
    <div className="u-small-spacing-top" key="invites-pending">
      <PendingInvites inviteEmail={BenchlingConfig.INVITE_EMAIL} />
    </div>
  ) : undefined,
];

const handleClickCreate = (query: string): void => {
  const organization = {name: query};
  OrganizationActions.openCreateOrganizationPage(organization);
};

function CreateOrganizationFooter(props: {query: string}): JSX.Element {
  return (
    <div className="text-center u-small-padding-top u-tiny-padding-left">
      <a
        style={{fontSize: '14px'}}
        onClick={() => {
          handleClickCreate(props.query);
        }}
        {...getTestProps({component: 'SearchOrganizationModal', element: 'create'})}
      >
        Create an organization
      </a>
    </div>
  );
}

interface SearchOrganizationModalProps {
  currentUser: EntityStoreUser;
  onRequestClose: () => void;
}

export default function SearchOrganizationModal(props: SearchOrganizationModalProps): JSX.Element {
  const [
    {query, filters, hasSearched, requestedJoinOrganizationIds},
    {onChangeQuery, onChangeFilters, onChangeRequestedJoinOrganizationIds},
  ] = useJoinOrganizationSearch();

  // TODO(Steven): We're currently relying on the entity store to tell us whether a user has organization
  //  inbound and outbound requests. Ideally, we'd move this responsibility to the JoinOrganizationSearch
  //  component and pull this data with GraphQL.
  useEffect(() => {
    if (props.currentUser.currentUserOutboundRequests) {
      onChangeRequestedJoinOrganizationIds((requestedJoinOrganizationIds) => [
        ...requestedJoinOrganizationIds,
        ...getOrgIdsWithUserJoinRequest(),
      ]);
    }
  }, [props.currentUser.currentUserOutboundRequests, onChangeRequestedJoinOrganizationIds]);

  analytics.track('onboarding', 'opened_search_organization_modal');
  useEffect(() => {
    UserActions.loadOrgRequests();
    // Any requests made from this modal will be stored in local state while the modal is open but will not
    // be synced to the EntityStore, so to ensure consistent UI state pending requests must be refetched from
    // the backend on subsequent mounts.
  }, []);

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleClose = () => {
    props.onRequestClose();
    OrganizationActions.resetSearchOrgModalState();
  };

  const showInstitutionFilter = BenchlingConfig.GROWTH_ENABLE_ORGANIZATION_RECOMMENDATIONS;
  const showRecommendations = BenchlingConfig.GROWTH_ENABLE_ORGANIZATION_RECOMMENDATIONS;

  const canCreateOrg = !BenchlingConfig.PRIVATE_DEPLOY || props.currentUser.isTenantAdmin;

  return (
    <Modal
      onRequestClose={handleClose}
      closeable={true}
      title="Search for an organization to join"
      wrapInForm={false}
      footer={canCreateOrg ? <CreateOrganizationFooter query={query} /> : undefined}
      showFooterBorder={true}
      footerPullRight={true}
      buttons={canCreateOrg ? [] : undefined}
    >
      {(Boolean(props.currentUser.currentUserInboundRequests?.length) ||
        Boolean(props.currentUser.pendingOrgInvites)) && (
        <div>
          {renderPendingInvites(props.currentUser)}
          <hr />
        </div>
      )}
      <p>
        {`Organizations are a great way to collaborate with people on Benchling. \
  Search for the name of your organization to get started.`}
      </p>
      <JoinOrganizationSearch
        query={query}
        onChangeQuery={onChangeQuery}
        filters={filters}
        setFilters={onChangeFilters}
        requestedJoinOrganizationIds={requestedJoinOrganizationIds}
        onChangeRequestedJoinOrganizationIds={onChangeRequestedJoinOrganizationIds}
        canNavigateToOrganizationPage={true}
        showInstitutionFilter={showInstitutionFilter}
        hasSearched={hasSearched}
        showRecommendations={showRecommendations}
      />
    </Modal>
  );
}
